import React, { useMemo, useState, useCallback } from "react";
import moment from "moment";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { CardContainer } from "@src/components/cards";
import { Button, Checkbox, Loader, Tooltip, Icon } from "@src/components/atoms";
import QueryResults from "./QueryResults";
import {
  preloadBehaviourMetadataFields,
  getBehaviourMetadataFieldsSync,
} from "@src/components/graphs/behaviourFields";
import {
  usePageViewTracker,
  trackActionActivity,
} from "@src/utils/userActivityTracker";
import { SignolUtils } from "@src/utils";

export const DataAccessPage = () => {
  const { profile } = useManagerContext();
  const auth = useAuthContext();
  const { getDynamicReplacement } = useDCContext();

  // Track page view
  usePageViewTracker(auth);

  // Download mutation hook
  const downloadMutation = ManagerHooks.useDownloadDataQuery(auth.activeRole);

  // Map profile behaviours to the format needed for the UI
  const SPECIFIC_BEHAVIORS = useMemo(
    () =>
      (profile.behaviours || []).map((b) => ({
        id: b.behaviourAcronym,
        label: b.behaviourName,
      })),
    [profile.behaviours],
  );

  // Preload all behavior metadata fields on mount
  React.useEffect(() => {
    const behaviorAcronyms = SPECIFIC_BEHAVIORS.map((b) => b.id);
    if (behaviorAcronyms.length > 0) {
      preloadBehaviourMetadataFields(behaviorAcronyms);
    }
  }, [SPECIFIC_BEHAVIORS]);

  // Calculate default dates
  const today = new Date().toISOString().split("T")[0];
  const lastYear = new Date(
    new Date().setFullYear(new Date().getFullYear() - 1),
  )
    .toISOString()
    .split("T")[0];

  // Date state
  const [dateFrom, setDateFrom] = useState(lastYear);
  const [dateTo, setDateTo] = useState(today);

  // Editor Mode: "basic" (Visual Builder) or "advanced" (SQL Editor)
  const [editorMode, setEditorMode] = useState("basic");

  // Prefetch query to get schema (runs once on mount, results NOT displayed)
  const previewSql = useMemo(
    () =>
      `SELECT * FROM signol_app WHERE ocurrence_datetime::date >= '${lastYear}' AND ocurrence_datetime::date <= '${today}' LIMIT 100`,
    [lastYear, today],
  );

  const {
    data: previewData,
    isPending: isPendingPreview,
    error: previewError,
  } = ManagerHooks.useExecuteDataQuery(
    auth.activeRole,
    previewSql,
    true, // Always enabled - this runs on mount
    editorMode,
  );

  // Track user-triggered query
  const [shouldExecute, setShouldExecute] = useState(false);
  const [hasExecutedQuery, setHasExecutedQuery] = useState(false);
  const [generatedSql, setGeneratedSql] = useState("");

  // Execute user query (only when user clicks "Fetch data")
  const {
    data: queryResults,
    isFetching: isPendingQuery,
    error: queryError,
  } = ManagerHooks.useExecuteDataQuery(
    auth.activeRole,
    generatedSql,
    shouldExecute,
    editorMode,
  );

  // Create mappings from schema: name (for SQL) -> alias (for display)
  // Use previewData schema (from prefetch) to build checkboxes
  const { columnNames, columnAliases, aliasToName } = useMemo(() => {
    if (!previewData?.schema) {
      return { columnNames: [], columnAliases: {}, aliasToName: {} };
    }

    const names = previewData.schema.map((col) => col.name);
    const aliases = {};
    const reverseMap = {};

    previewData.schema.forEach((col) => {
      // Use alias if available, otherwise fall back to name
      aliases[col.name] = col.alias || col.name;
      if (col.alias) {
        reverseMap[col.alias] = col.name;
      }
    });

    return {
      columnNames: names,
      columnAliases: aliases,
      aliasToName: reverseMap,
    };
  }, [previewData?.schema]);

  // Use column names (not aliases) for internal logic
  const availableColumns = useMemo(() => columnNames, [columnNames]);

  // Get all behavior acronyms for filtering
  const behaviorAcronyms = useMemo(
    () => SPECIFIC_BEHAVIORS.map((b) => b.id.toUpperCase()),
    [SPECIFIC_BEHAVIORS],
  );

  // Split columns into 3 categories (default, behaviour, specific)
  const { defaultColumns, behaviorColumns, specificColumns } = useMemo(() => {
    // Default: first 7 columns
    const defaults = availableColumns.slice(0, 7);

    // Behavior columns: start with any behavior acronym + "__"
    const behaviors = availableColumns.filter((col) =>
      behaviorAcronyms.some((acronym) =>
        col.toUpperCase().startsWith(`${acronym}__`),
      ),
    );

    // Specific: remaining columns (excluding defaults and behavior columns)
    const defaultSet = new Set(defaults);
    const behaviorSet = new Set(behaviors);
    const specific = availableColumns.filter(
      (col) => !defaultSet.has(col) && !behaviorSet.has(col),
    );

    return {
      defaultColumns: defaults,
      behaviorColumns: behaviors,
      specificColumns: specific,
    };
  }, [availableColumns, behaviorAcronyms]);

  // State management
  const placeholderSQLQuery = "SELECT * FROM signol_app LIMIT 100";
  const [defaultFields, setDefaultFields] = useState([]);
  const [selectedBehaviors, setSelectedBehaviors] = useState([]);
  const [showSpecificFields, setShowSpecificFields] = useState(false);
  const [specificFields, setSpecificFields] = useState([]);
  const [specificFieldsSearch, setSpecificFieldsSearch] = useState("");
  const [headersFormat, setHeadersFormat] = useState("alias"); // "raw" or "alias"
  const [rawSqlQuery, setRawSqlQuery] = useState(placeholderSQLQuery);
  const [hasModifiedSelection, setHasModifiedSelection] = useState(false);

  // Initialize default fields when columns are loaded
  React.useEffect(() => {
    if (defaultColumns.length > 0 && defaultFields.length === 0) {
      setDefaultFields(defaultColumns);
    }
  }, [defaultColumns]);

  const handleToggleDefaultField = useCallback((field) => {
    setDefaultFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
    setHasModifiedSelection(true);
  }, []);

  const handleToggleBehavior = useCallback(
    (behaviorId) => {
      const isCurrentlySelected = selectedBehaviors.includes(behaviorId);

      // Toggle behavior selection
      setSelectedBehaviors((prev) =>
        isCurrentlySelected
          ? prev.filter((id) => id !== behaviorId)
          : [...prev, behaviorId],
      );

      // Auto-select/deselect associated metadata fields (using sync version since preloaded)
      const metadataFields = getBehaviourMetadataFieldsSync(behaviorId);
      // Keep only metadata fields that exist in specificColumns
      const validMetadataFields = metadataFields.filter((field) =>
        specificColumns.includes(field),
      );

      // Also include any fields in specificColumns that contain the behavior acronym
      const behaviorRelatedFields = specificColumns.filter(
        (field) =>
          field.toLowerCase().includes(`_${behaviorId.toLowerCase()}_`) ||
          field.toLowerCase().startsWith(`${behaviorId.toLowerCase()}_`),
      );

      // Combine both sets (metadata graph fields + behavior-related fields)
      const allRelatedFields = [
        ...new Set([...validMetadataFields, ...behaviorRelatedFields]),
      ];

      setSpecificFields((prev) => {
        if (isCurrentlySelected) {
          // Deselecting behavior - remove its related fields
          return prev.filter((field) => !allRelatedFields.includes(field));
        } else {
          // Selecting behavior - add its related fields (avoid duplicates)
          const newFields = allRelatedFields.filter(
            (field) => !prev.includes(field),
          );
          return [...prev, ...newFields];
        }
      });

      setHasModifiedSelection(true);
    },
    [selectedBehaviors, specificColumns],
  );

  const handleSelectAllBehaviors = useCallback(() => {
    if (selectedBehaviors.length === SPECIFIC_BEHAVIORS.length) {
      setSelectedBehaviors([]);
    } else {
      setSelectedBehaviors(SPECIFIC_BEHAVIORS.map((b) => b.id));
    }
    setHasModifiedSelection(true);
  }, [selectedBehaviors.length, SPECIFIC_BEHAVIORS]);

  const handleToggleSpecificField = useCallback((field) => {
    setSpecificFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field],
    );
    setHasModifiedSelection(true);
  }, []);

  // Filter specific columns based on search
  const filteredSpecificColumns = useMemo(() => {
    if (!specificFieldsSearch) return specificColumns;
    const searchLower = specificFieldsSearch.toLowerCase();
    return specificColumns.filter((field) => {
      const alias = columnAliases[field] || field;
      return (
        field.toLowerCase().includes(searchLower) ||
        alias.toLowerCase().includes(searchLower)
      );
    });
  }, [specificColumns, specificFieldsSearch, columnAliases]);

  const handleFetchData = () => {
    let sql;
    let allColumns = [];

    if (editorMode === "advanced") {
      // SQL Editor mode - use raw SQL query
      sql = rawSqlQuery;
    } else {
      // Visual Builder mode - generate SQL from selections
      // Collect all selected columns
      allColumns = [...defaultFields];

      // Add behavior-specific columns (columns starting with behavior_acronym__)
      selectedBehaviors.forEach((behaviorId) => {
        const behaviorColumns = availableColumns.filter((col) =>
          col.toLowerCase().startsWith(`${behaviorId.toLowerCase()}__`),
        );
        allColumns = [...allColumns, ...behaviorColumns];
      });

      // Add manually selected specific fields
      allColumns = [...allColumns, ...specificFields];

      // Remove duplicates
      const uniqueColumns = [...new Set(allColumns)];

      // Sort columns to match original schema order
      const sortedColumns = uniqueColumns.sort((a, b) => {
        return availableColumns.indexOf(a) - availableColumns.indexOf(b);
      });

      const columns = sortedColumns.join(", ");
      sql = `SELECT ${columns} FROM signol_app WHERE ocurrence_datetime::date >= '${dateFrom}' AND ocurrence_datetime::date <= '${dateTo}'`;
    }

    setGeneratedSql(sql);
    setShouldExecute(true);
    setHasExecutedQuery(true);
    setHasModifiedSelection(false); // Reset flag after fetching

    trackActionActivity("MANAGER_DATA_DOWNLOAD_PREVIEW", {
      query: sql,
      columns: allColumns,
      dateFrom,
      dateTo,
      editorMode,
    });
  };

  // Reset execution flag after query completes
  React.useEffect(() => {
    if (queryResults && shouldExecute) {
      setShouldExecute(false);
    }
  }, [queryResults, shouldExecute]);

  // Track SQL editor changes
  const handleSqlQueryChange = useCallback((e) => {
    setRawSqlQuery(e.target.value);
    setHasModifiedSelection(true);
  }, []);

  // Track date changes
  const handleDateFromChange = useCallback((e) => {
    setDateFrom(e.target.value);
    setHasModifiedSelection(true);
  }, []);

  const handleDateToChange = useCallback((e) => {
    setDateTo(e.target.value);
    setHasModifiedSelection(true);
  }, []);

  // Handle download CSV button click
  const handleDownloadCSV = useCallback(async () => {
    try {
      const result = await downloadMutation.mutateAsync({
        sql: generatedSql,
        headersFormat,
      });

      if (result.download_link) {
        trackActionActivity("MANAGER_DATA_DOWNLOAD_CSV", {
          generatedSql,
          editorMode,
          headersFormat,
          downloadLink: result.download_link,
        });

        // download file
        window.open(result.download_link, "_blank");
      }
    } catch (error) {
      console.error("Download failed:", error);
    }
  }, [downloadMutation, generatedSql, headersFormat, editorMode]);

  // Only show data from user-triggered query, NOT from prefetch
  const displayData = hasExecutedQuery ? queryResults : null;
  const isLoadingData = isPendingQuery;

  const infoHeaderTooltip = [
    getDynamicReplacement("fuel_manager__tooltip__data_download_header", {}),
  ];

  const minSelectionDatePossible =
    profile.customer.firstEventDate?.split("T")[0] || "2020-01-01";

  return (
    <div>
      <div className="bg-primary-200 p-12">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-primary-900 text-2xl font-bold mb-3">
              Download your processed data
            </h1>
            <p className="text-primary-900">
              Signol combines and standardises data from multiple sources to
              facilitate thorough data analysis. Access your processed data to
              explore and validate your own results.
            </p>
          </div>
          <Tooltip
            direction="down-left"
            absolute={false}
            content={infoHeaderTooltip}
          />
        </div>
      </div>

      <div className="px-14 py-10">
        {/* Editor Mode Toggle (only available for admins) */}
        {!!auth.impersonatedSessionContext && (
          <>
            <p className="text-xs text-primary-600 mb-2">
              (Advanced mode is only available for administrators)
            </p>
            <div className="mb-6 flex items-center gap-2 bg-white rounded-lg p-1 border border-gray-300 w-fit">
              <button
                onClick={() => setEditorMode("basic")}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                  editorMode === "basic"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Visual Builder
              </button>
              <button
                onClick={() => setEditorMode("advanced")}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                  editorMode === "advanced"
                    ? "bg-primary-500 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                SQL Editor
              </button>
            </div>
          </>
        )}

        {editorMode === "advanced" ? (
          /* SQL Editor Mode */
          <div className="mb-6">
            <h2 className="text-xl font-bold mb-3">SQL Query</h2>
            <p className="text-sm text-gray-600 mb-3">
              Write your custom SQL query below
            </p>
            <textarea
              value={rawSqlQuery}
              onChange={handleSqlQueryChange}
              className="w-full h-64 p-4 font-mono text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder={placeholderSQLQuery}
            />
            {/* Show query error for advanced mode */}
            {hasExecutedQuery && queryError && (
              <div className="mt-3 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">Query Failed</span>
                </div>
                <p className="mt-1 text-sm">
                  {queryError.message ||
                    "Invalid SQL query. Please check your syntax and try again."}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Visual Builder Mode */
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3">Time Period</h2>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <span>From</span>
                  <input
                    type="date"
                    min={minSelectionDatePossible}
                    max={today}
                    value={dateFrom}
                    onChange={handleDateFromChange}
                    className="border border-gray-300 rounded-md px-4 py-2 text-sm"
                  />
                </label>
                <label className="flex items-center gap-2">
                  <span>To</span>
                  <input
                    type="date"
                    min={minSelectionDatePossible}
                    max={today}
                    value={dateTo}
                    onChange={handleDateToChange}
                    className="border border-gray-300 rounded-md px-4 py-2 text-sm"
                  />
                </label>
              </div>
              <p className="text-xs text-primary-600 my-3">
                * Launched with Signol:{" "}
                {moment
                  .utc(profile.customer.contractStartDate)
                  .format("DD MMMM YYYY")}
              </p>
            </div>

            {isPendingPreview ? (
              <Loader
                size="xl"
                text="Loading available fields..."
                textColor="black"
              />
            ) : previewError ? (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">
                    Failed to load data fields
                  </span>
                </div>
                <p className="mt-1 text-sm">
                  {previewError.message || "Please try refreshing the page."}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-3">
                    Default Data Fields
                  </h2>
                  <div className="grid grid-cols-2 gap-x-6 -my-2">
                    {defaultColumns.map((field) => (
                      <div key={field} className="-my-2">
                        <Checkbox
                          id={`field-${field}`}
                          name={field}
                          checked={defaultFields.includes(field)}
                          onChange={() => handleToggleDefaultField(field)}
                          label={columnAliases[field] || field}
                          fontSize="sm"
                          type="neutral"
                          maxWidthMd={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h2 className="text-xl font-bold mb-2">Specific Analysis</h2>
                  <p className="text-gray-600 text-sm mb-3">
                    Choose data specific to a Signol behaviour:
                  </p>
                  <div className="my-0">
                    <Checkbox
                      id="select-all-behaviors"
                      name="select-all"
                      checked={
                        selectedBehaviors.length === SPECIFIC_BEHAVIORS.length
                      }
                      onChange={handleSelectAllBehaviors}
                      label="Select all"
                      fontSize="sm"
                      type="neutral"
                      maxWidthMd={false}
                    />

                    {SPECIFIC_BEHAVIORS.map((behavior) => (
                      <Checkbox
                        key={behavior.id}
                        id={`behavior-${behavior.id}`}
                        name={behavior.id}
                        checked={selectedBehaviors.includes(behavior.id)}
                        onChange={() => handleToggleBehavior(behavior.id)}
                        label={behavior.label}
                        fontSize="sm"
                        type="neutral"
                        maxWidthMd={false}
                      />
                    ))}
                  </div>

                  <div className="my-8">
                    <button
                      onClick={() => setShowSpecificFields(!showSpecificFields)}
                      className="text-gray-700 hover:text-primary-600 hover:underline flex items-center gap-1 text-sm cursor-pointer"
                    >
                      <span>Or select specific fields</span>
                      <Icon
                        name={showSpecificFields ? "chevronUp" : "chevronDown"}
                        size="sm"
                        backgroundColor="transparent"
                        textColor="gray-700"
                        padding={false}
                      />
                    </button>
                  </div>

                  {showSpecificFields && (
                    <div className="px-4 py-3 bg-gray-100 rounded">
                      <input
                        type="text"
                        placeholder="Search fields..."
                        value={specificFieldsSearch}
                        onChange={(e) =>
                          setSpecificFieldsSearch(e.target.value)
                        }
                        className="w-full px-3 py-2 mb-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="max-h-96 overflow-y-auto">
                        <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                          {filteredSpecificColumns.map((field) => (
                            <div key={field} className="-my-1">
                              <Checkbox
                                id={`specific-${field}`}
                                name={field}
                                checked={specificFields.includes(field)}
                                onChange={() =>
                                  handleToggleSpecificField(field)
                                }
                                label={columnAliases[field] || field}
                                fontSize="xs"
                                type="neutral"
                                maxWidthMd={false}
                              />
                            </div>
                          ))}
                        </div>
                        {filteredSpecificColumns.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">
                            No fields match your search
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        <div className="max-w-48">
          <Button
            onClick={handleFetchData}
            disabled={editorMode === "basic" && defaultFields.length === 0}
            loading={isPendingQuery}
            color="primary"
            block={true}
            padding={false}
            className="mb-8"
            {...SignolUtils.makeTrackableDataAttributes({
              actionKey: "MANAGER_DATA_DOWNLOAD_PREVIEW_BUTTON",
              query: generatedSql,
            })}
          >
            {hasExecutedQuery && hasModifiedSelection
              ? "Refresh Data"
              : "Fetch Data"}
          </Button>
        </div>

        {/* Preview Section */}
        {(displayData || isLoadingData) && (
          <div className="bg-primary-200 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold mb-1">Preview</h2>
                <p className="text-primary-900 text-sm">
                  Each row represents an event for a single officer
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white rounded-md p-1 border border-gray-300">
                <button
                  onClick={() => setHeadersFormat("alias")}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    headersFormat === "alias"
                      ? "bg-primary-500 text-white font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Alias
                </button>
                <button
                  onClick={() => setHeadersFormat("raw")}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    headersFormat === "raw"
                      ? "bg-primary-500 text-white font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Raw
                </button>
              </div>
            </div>

            {isLoadingData ? (
              <div className="flex justify-center items-center py-20">
                <Loader size="xl" text="Fetching data..." textColor="black" />
              </div>
            ) : (
              <QueryResults
                results={displayData}
                error={queryError}
                isLoading={false}
                columnAliases={columnAliases}
                headersFormat={headersFormat}
                onDownloadCSV={hasExecutedQuery ? handleDownloadCSV : null}
                downloadLoading={downloadMutation.isPending}
                downloadError={downloadMutation.error}
                totalRowCount={
                  hasExecutedQuery ? queryResults?.total_count : null
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataAccessPage;
